import React, { ReactNode, useEffect, useState } from "react";
import {
  Flex,
  Icon,
  IconTypes,
  RadioGroup,
  SearchableDropdown,
  StyledText,
  Types
} from "@emonvia/web-components";
import ValidatedTextInput from "@/components/ValidatedTextInput";
import theme from "@/utils/theme/theme";
import { IOnboardingConnector } from "@/api/ApiTypes";
import { useTranslation } from "@/utils/translations/Translator";
import useEvseIdValidation from "@steps/ConnectorEvseID/hook/useEvseIdValidation";
import QrCodeScanner from "@/components/QRCodeScanner/QRCodeScanner";
import { Card } from "@/components/Card/styles";
import { Control, Controller } from "react-hook-form";
import { ConnectorDetailsSchema, phaseRotationsSchema } from "@/schemas/ConnectorDetails";
import { IEmsConnectorInfo_MaxLoadUnit } from "@/api/Enums";

interface IProps {
  connector: IOnboardingConnector;
  connectors: IOnboardingConnector[];
  onEvseIdChange: (index: number, newEvseId: string, isValid: boolean) => void;
  index: number;
  control: Control<ConnectorDetailsSchema>;
}

const phaseRotationMap: Record<string, ReactNode> = {
  L1: <StyledText text="L1" type={Types.ns_16_sb} color="#7B5C45" />, // TODO: update color name as soon as we have a variable for it in theme
  L2: <StyledText text="L2" type={Types.ns_16_sb} color={theme.colors.primaryBlack} />,
  L3: <StyledText text="L3" type={Types.ns_16_sb} color={theme.colors.grey650} />
};

const phaseRotations = phaseRotationsSchema.options.map((option) => {
  const phases = option.match(/L\d+/g);
  return {
    label: (
      <Flex gap={8}>
        {phases.map((phase) => (
          <>{phaseRotationMap[phase]}</>
        ))}
      </Flex>
    ),
    value: option
  };
});

// simple map for max load unit with out translation on purpose
// unit are always the same after translation in any language
const maxLoadUnitLabel: Record<IEmsConnectorInfo_MaxLoadUnit, string> = {
  [IEmsConnectorInfo_MaxLoadUnit.A]: "A",
  [IEmsConnectorInfo_MaxLoadUnit.KW]: "kW"
};

const maxLoadUnits = Object.values(IEmsConnectorInfo_MaxLoadUnit).map((option) => ({
  label: maxLoadUnitLabel[option],
  value: option
}));

const UpdateConnector: React.FC<IProps> = ({
  connector: { connectorIdentifier, evseId: savedEvseId },
  connectors,
  onEvseIdChange,
  index,
  control
}) => {
  const { t } = useTranslation();
  const [showQRModal, setShowQRModal] = useState<boolean>(false);
  const { evseId, errorMessage, handleChange, isValid, setErrorMessage } = useEvseIdValidation(
    savedEvseId || "",
    connectors
  );

  useEffect(() => {
    onEvseIdChange(index, evseId, isValid);
  }, [evseId, isValid, index]);

  const handleInputChange = (newEvseId: string) => {
    handleChange(newEvseId);
  };

  return (
    <Card>
      <StyledText
        data-testid="UpdateConnector_EvseInputLabel"
        text={`${t("CONNECTOR")} ${connectorIdentifier}`}
        type={Types.ns_18_sb}
        color={theme.colors.primaryBlack}
        margin={[0, 0, 20]}
      />
      <Flex flexDirection="column" gap={10}>
        <Flex flexDirection="column" width="100%">
          <StyledText
            data-testid="UpdateConnector_EvseInputLabel"
            text={t("EVSE_ID")}
            type={Types.ns_16_l}
            color={theme.colors.primaryBlack}
            margin={[0, 0, 8]}
          />
          <ValidatedTextInput
            key={index}
            data-testid={`UpdateConnector_EvseIdInput${connectorIdentifier}`}
            value={evseId}
            onChange={handleInputChange}
            placeholder={t("SCAN_OR_ENTER_5_DIGIT_EVSEID")}
            validators={["removeSpaces"]}
            valid={!!evseId && !errorMessage}
            error={!!errorMessage}
            errorMessage={errorMessage}
            cameraIcon={
              <Icon
                size={24}
                type={IconTypes.camera}
                style={{ marginRight: 12, color: theme.colors.blue500, cursor: "pointer" }}
                onClick={() => setShowQRModal(true)}
              />
            }
          />
          <QrCodeScanner
            key={connectorIdentifier}
            title={`${t("CONNECTOR")} ${connectorIdentifier}`}
            onChange={(val: string) => handleChange(val)}
            showModal={showQRModal}
            setShowModal={setShowQRModal}
            setErrorMessage={setErrorMessage}
          />
        </Flex>
        <Flex flexDirection="column">
          <StyledText
            data-testid="UpdateConnector_EvseInputLabel"
            text={t("PHASE_ROTATION")}
            type={Types.ns_16_l}
            color={theme.colors.primaryBlack}
            margin={[0, 0, 8]}
          />
          <Controller
            control={control}
            name={`connectors.${index}.emsConnectorInfo.phaseRotation`}
            render={({ field, fieldState }) => (
              <SearchableDropdown
                selected={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={phaseRotationsSchema.enum["L1L2L3"]}
                valid={fieldState.isDirty && !fieldState.isValidating && !fieldState.invalid}
                options={phaseRotations}
              />
            )}
          />
        </Flex>

        <Flex flexDirection="column">
          <StyledText
            data-testid="UpdateConnector_EvseInputLabel"
            text={t("MAX_CONNECTOR_LOAD")}
            type={Types.ns_16_l}
            color={theme.colors.primaryBlack}
            margin={[0, 0, 8]}
          />
          <Flex flexDirection="row" gap={12} alignItems={"flex-start"}>
            <Controller
              control={control}
              name={`connectors.${index}.emsConnectorInfo.maxLoad`}
              render={({ field, fieldState }) => (
                <ValidatedTextInput
                  value={field.value?.toString() ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  width={150}
                  valid={fieldState.isDirty && !fieldState.isValidating && !fieldState.invalid}
                  placeholder="32"
                />
              )}
            />

            <Flex flexDirection="row" height="42px">
              <Controller
                control={control}
                name={`connectors.${index}.emsConnectorInfo.maxLoadUnit`}
                render={({ field }) => (
                  <RadioGroup
                    options={maxLoadUnits}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};

export default UpdateConnector;
